<template>
  <div>
  <!-- Add API status notification above the form -->
  <div v-if="showApiStatusNotification" class="erc-api-status-notification">
    <div class="erc-api-status-icon">!</div>
    <div class="erc-api-status-content">
      <h4 class="erc-api-status-title">API Key Error</h4>
      <p class="erc-api-status-message">{{ apiKeyStatus.message }}</p>
    </div>
  </div>
  <!-- Bind theme class here -->
  <div :class="['erc-vehicle-catalog-container', currentTheme]" id="erc-vehicle-catalog-container">
    <div class="form">
      <vehicle-catalog-form 
        @getRemaps="getRemaps" 
        @update-visual-grids="handleGridDataUpdate"
        @selection-changed-stale-remap="handleStaleRemapData"
        ref="vehicleCatalogFormRef"
      />
    </div>

    <!-- Visual Selection Grids - Rendered by VehicleCatalog, data from Form.vue -->
    <div v-if="gridRenderData && !gridRenderData.isPrefilling && !gridRenderData.form.engine" class="visual-selection-grids-wrapper">
      <!-- Vehicle Types Grid -->
      <div v-if="!gridRenderData.form.vehicleType && gridRenderData.vehicleTypes && gridRenderData.vehicleTypes.length > 0" class="grid-section">
    <h4 class="grid-title">Select Vehicle Type:</h4>
    <div class="item-grid">
        <!-- REPLACE the content of the v-for with this -->
        <div v-for="item in gridRenderData.vehicleTypes" :key="`grid-type-${item.id}`" class="grid-item grid-item--card" @click="triggerFormGridSelection('vehicleType', item.id)">
            <div class="grid-item-icon-wrapper">
            <img v-if="item.icon" :src="item.icon" :alt="item.name" class="grid-item-icon">
                <!-- Optional: Add a placeholder for types without an icon -->
                <div v-else class="grid-item-icon-placeholder">?</div>
            </div>
            <span class="grid-item-name">{{ item.name }}</span>
        </div>
    </div>
</div>

      <!-- Brands Grid -->
      <div v-if="gridRenderData.form.vehicleType && !gridRenderData.form.brand && gridRenderData.brands && gridRenderData.brands.length > 0" class="grid-section">
        <h4 class="grid-title">Select Brand:</h4>
        <div class="item-grid">
          <div v-for="item in gridRenderData.brands" :key="`grid-brand-${item.id}`" class="grid-item" @click="triggerFormGridSelection('brand', item.id)">
            <img v-if="item.logo" :src="item.logo" :alt="cleanBrandName(item.name)" class="grid-item-logo">
            <span class="grid-item-name">{{ cleanBrandName(item.name) }}</span>
          </div>
        </div>
      </div>

      <!-- Models Grid -->
      <div v-if="gridRenderData.form.brand && !gridRenderData.form.model && gridRenderData.models && gridRenderData.models.length > 0" class="grid-section">
        <h4 class="grid-title">Select Model:</h4>
        <div class="item-grid item-grid--images">
          <div v-for="item in gridRenderData.models" :key="`grid-model-${item.id}`" class="grid-item grid-item--card" @click="triggerFormGridSelection('model', item.id)">
            <img v-if="item.image" :src="item.image" :alt="item.name" class="grid-item-image">
            <div v-else class="grid-item-image-placeholder">No Image</div>
            <span class="grid-item-name">{{ item.name }}</span>
          </div>
        </div>
      </div>

      <!-- Generations Grid -->
      <div v-if="gridRenderData.form.model && !gridRenderData.form.generation && gridRenderData.generations && gridRenderData.generations.length > 0" class="grid-section">
        <h4 class="grid-title">Select Generation:</h4>
        <div class="item-grid item-grid--images">
          <div v-for="item in gridRenderData.generations" :key="`grid-gen-${item.id}`" class="grid-item grid-item--card" @click="triggerFormGridSelection('generation', item.id)">
            <img v-if="item.image" :src="item.image" :alt="item.name" class="grid-item-image">
            <div v-else class="grid-item-image-placeholder">No Image</div>
            <span class="grid-item-name">
              {{ item.name }}
              <span v-if="item.year" class="grid-item-year">
                ({{ item.year }}{{ item.yearend ? ' - ' + item.yearend : '' }})
              </span>
            </span>
          </div>
        </div>
      </div>

      <!-- Engines Grid (Grouped by Fuel Type) -->
      <div v-if="gridRenderData.form.generation && !gridRenderData.form.engine && groupedEngines && Object.keys(groupedEngines).length > 0" class="grid-section">
        <div v-for="(enginesInGroup, fuelType) in groupedEngines" :key="fuelType" class="fuel-group-section">
          <h4 class="grid-title grid-title--fuel-group">Select {{ fuelType }} Engine:</h4>
          <div class="item-grid item-grid--engines">
            <div v-for="engine in enginesInGroup" :key="`grid-engine-${engine.id}`" class="grid-item grid-item--engine" @click="triggerFormGridSelection('engine', engine.id)">
              <span class="grid-item-name">{{ engine.name }}</span>
              <!-- You can add more engine details here if needed -->
            </div>
          </div>
        </div>
      </div>
    </div>

    <remap-info :vehicle-data="vehicleData" :api-key-status="apiKeyStatus"/>
  </div>
  </div>
</template>   

<script>
import VehicleCatalogForm from "@/components/Form";
import RemapInfo from "@/components/RemapInfo";

export default {
  name: 'VehicleCatalog',
  components: {
    'vehicle-catalog-form': VehicleCatalogForm,
    'remap-info': RemapInfo,
  },
  data() {
    return {
      vehicleData: {},
      currentTheme: 'theme-light', // Default theme class
      gridRenderData: null, // To store data for visual grids
      apiKeyStatus: { valid: true, message: '' }
    }
  },
  computed: {
    groupedEngines() {
      if (!this.gridRenderData || !this.gridRenderData.engines || this.gridRenderData.engines.length === 0) {
        return null;
      }
      return this.gridRenderData.engines.reduce((acc, engine) => {
        const fuelTypeName = engine.fuel_type_name || 'Other'; // Assuming engine object has fuel_type_name
        if (!acc[fuelTypeName]) {
          acc[fuelTypeName] = [];
        }
        acc[fuelTypeName].push(engine);
        return acc;
      }, {});
    },
    showApiStatusNotification() {
      // Hide notification for local dev (localhost or 127.0.0.1)
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      return !isLocal && this.apiKeyStatus && !this.apiKeyStatus.valid;
    }
  },
  mounted() {
    console.log("VehicleCatalog.vue mounted. Initializing theme and colors.");
    // Apply settings when component is mounted
    if (window.ercData && window.ercData.settings) {
      const settings = window.ercData.settings;

      // 1. Set Theme Class
      this.currentTheme = `theme-${settings.theme || 'light'}`;

       // 2. Determine color values from settings with fallbacks
      // Instead of this.$el, use a selector for the main container
      const rootEl = this.$el.querySelector('.erc-vehicle-catalog-container');
      if (rootEl) {
        // Using fallbacks here for extra safety - requires defaults passed in ercData
        // Ensure settings object exists, even if empty, to avoid errors accessing its properties
        const currentSettings = window.ercData?.settings || {};

        // Determine the values first, using plugin settings and then hardcoded fallbacks
        const oemVal = currentSettings.color_oem || currentSettings.default_color_oem || '#00aeff';
        const st1Val = currentSettings.color_st1 || currentSettings.default_color_st1 || '#ff5419';
        const st2Val = currentSettings.color_st2 || currentSettings.default_color_st2 || '#e82409';

        // Set the -val variables (as currently done, for consistency or if other CSS relies on them)
        rootEl.style.setProperty('--erc-color-oem-val', oemVal);
        rootEl.style.setProperty('--erc-color-st1-val', st1Val);
        rootEl.style.setProperty('--erc-color-st2-val', st2Val);

        // ALSO set the main variables directly using these determined values.
        // This makes them available as inline styles even if the CSS var(--name-val) indirection fails.
        rootEl.style.setProperty('--erc-color-oem', oemVal);
        rootEl.style.setProperty('--erc-color-st1', st1Val);
        rootEl.style.setProperty('--erc-color-st2', st2Val);
      }
    } else {
        console.warn("ERC Catalogue: Settings data not found (ercData.settings). Using defaults.");
        // Optionally set default CSS variables here if needed, though CSS fallback should work
    }

    // Read API status from window.ercData if available
    if (window.ercData && window.ercData.api_status) {
      this.apiKeyStatus = window.ercData.api_status;
    }
  }, // Comma is correctly here
  methods: {
    getRemaps(formData) {
      this.vehicleData = {...formData}
    },
    handleStaleRemapData() {
      console.log("VehicleCatalog: Stale remap data signal received, clearing vehicleData.");
      this.vehicleData = {}; // Clear the data, RemapInfo will hide due to its v-if="showData"
    },
    handleGridDataUpdate(data) {
      this.gridRenderData = data;
    },
    triggerFormGridSelection(field, value) {
      if (this.$refs.vehicleCatalogFormRef && typeof this.$refs.vehicleCatalogFormRef.selectGridItem === 'function') {
        this.$refs.vehicleCatalogFormRef.selectGridItem(field, value);
      } else {
        console.error("Could not call selectGridItem on Form component via ref.");
      }
    },
    cleanBrandName(name) {
      if (typeof name !== 'string') return '';
      return name.replace(/\s*\(.*\)\s*$/, '').trim(); // Removes text in parentheses
    }
  }
} // End export default
</script>


<style lang="scss" scoped>
.erc-vehicle-catalog-container {
  box-sizing: border-box;
  max-width: 1140px;
  margin: 0 auto;

  // --- Define CSS Variables with Fallbacks ---
  --erc-color-oem-val: #00aeff; // Default OEM color
  --erc-color-st1-val: #ff5419; // Default ST1 color
  --erc-color-st2-val: #e82409; // Default ST2 color

  // Theme-independent variables using the settings
  --erc-color-oem: var(--erc-color-oem-val);
  --erc-color-st1: var(--erc-color-st1-val);
  --erc-color-st2: var(--erc-color-st2-val);

  // --- Light Theme (Default) Variables ---
  --erc-text-color: #333; // General text
  --erc-text-light: #54595f; // Lighter text
  --erc-text-heading: #000; // Headings
  --erc-bg-color: #fff; // Background for sections like remap info
  --erc-form-bg-color: var(--erc-color-oem); // Form background
  --erc-form-text-color: #fff; // Form label text
  --erc-select-bg-color: #fff; // Select background
  --erc-select-text-color: #000; // Select text
  --erc-select-border-color: #818a91; // Select border
  --erc-button-bg-color: #fff; // Button background
  --erc-button-text-color: var(--erc-color-oem); // Button text
  --erc-button-hover-bg-color: #00699b; // Button hover bg
  --erc-button-hover-text-color: #fff; // Button hover text
  --erc-border-color: #eee; // General borders
  --erc-donut-track-color: #ffffff; // White track for light theme
  --erc-donut-foreground-color: #f0f0f0; // Light grey foreground
  --erc-donut-center-bg: #ffffff; // Donut center background for light theme

  // --- MOVED DECLARATION ABOVE NESTED RULES ---
  color: var(--erc-text-color); // Apply general text color
  // background-color: var(--erc-bg-color); // Removed as requested

  * {
    box-sizing: border-box;
  }

  .form {
    padding: 30px;
    background-color: var(--erc-form-bg-color); // Use variable
    border-radius: 18px 18px 0 0;
    color: var(--erc-form-text-color); // Set base text color for form area
  }

} // End .erc-vehicle-catalog-container

// --- Dark Theme Overrides ---
.erc-vehicle-catalog-container.theme-dark {
  --erc-text-color: #ccc;
  --erc-text-light: #aaa;
  --erc-text-heading: #fff;
  --erc-bg-color: #000; // Dark background (if re-added)
  --erc-form-bg-color: var(--erc-color-oem);
  --erc-form-text-color: #ccc; // Adjust for contrast with OEM color if needed
  --erc-select-bg-color: #444;
  --erc-select-text-color: #eee;
  --erc-select-border-color: #666;
  --erc-button-bg-color: #555;
  --erc-button-text-color: #eee;
  --erc-button-hover-bg-color: #777;
  --erc-button-hover-text-color: #fff;
  --erc-border-color: #444;
  --erc-donut-track-color: #444; // Darker track for dark theme
  --erc-donut-foreground-color: #555; // Slightly lighter foreground
  --erc-donut-center-bg: #000000; // Donut center background for dark theme

  // Example: Adjust specific element colors if needed
  // .brand-name { color: var(--erc-text-heading); }
}

/* Styles for Visual Selection Grids Wrapper (in VehicleCatalog.vue) */
.visual-selection-grids-wrapper {
  padding: 20px 30px; // Match form padding for alignment, or adjust as needed
  margin-top: 0; // Adjust if spacing is needed between form and grids
  background-color: var(--erc-bg-color); // Use main background color, not form background
  color: var(--erc-text-color); // Use main text color
}

.grid-section {
  margin-bottom: 25px;
}

.grid-title {
  font-size: 1.1em; 
  color: var(--erc-text-heading); // Use heading color for titles
  margin-bottom: 12px;
  margin-top: 10px;
  font-weight: 500;

  &--fuel-group {
    font-size: 1em;
    margin-top: 15px; // Space above each fuel group title
    margin-bottom: 8px;
    color: var(--erc-text-color); // Slightly lighter for sub-grouping
  }
}

.item-grid {
  display: grid;
  gap: 12px; // Slightly increased gap
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));

  &--images {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
   &--engines {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); // Wider for engine names
  }
}

.grid-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border: 1px solid var(--erc-border-color); 
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s, box-shadow 0.2s;
  text-align: center;
  background-color: var(--erc-select-bg-color); // Use select background for consistency
  color: var(--erc-select-text-color);
  min-height: 60px; // Ensure a minimum height for text-only items

  &:hover {
    border-color: var(--erc-color-oem); 
    // background-color: color-mix(in srgb, var(--erc-select-bg-color, #ffffff) 90%, var(--erc-color-oem, #00aeff) 10%);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }

  
}

.grid-item .grid-item-image {
  width: 100%;
  height: 90px;
  object-fit: contain;
  margin-bottom: 8px;
  border-radius: 4px;
  background-color: #fff;
}

.grid-item .grid-item-image-placeholder {
  width: 100%;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e9ecef;
  color: #6c757d;
  font-size: 0.9em;
  margin-bottom: 8px;
  border-radius: 4px;
}

.grid-item .grid-item-name {
  font-size: 15px;
  font-weight: 300;
  line-height: 1.3;
}

.grid-item .grid-item-year {
  font-size: 0.8em;
  display: block;
  opacity: 0.8;
  margin-top: 2px;
}

.grid-item {
  &--card {
    justify-content: flex-start;
    gap: 10px;
    padding: 15px;
    transition: all 0.2s ease-in-out;

    &:hover {
      border-color: var(--erc-color-oem);
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
      transform: translateY(-2px);
    }
  }

  &--engine {
    min-height: auto;
    padding: 10px;
    justify-content: center;
  }
}

.grid-item-logo {
  max-height: 70px;
  max-width: 80%;
  margin-bottom: 8px;
  object-fit: contain;
}

.grid-item-icon-wrapper {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 5px;
}

.grid-item-icon {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.grid-item-icon-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
  border-radius: 4px;
  font-size: 24px;
  color: #aaa;
}

.grid-item-name {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}

/* Notification styles */
.erc-api-status-notification {
  display: flex;
  align-items: center;
  background-color: #fff3f3;
  border: 1px solid #ffcdd2;
  border-left: 5px solid #e53935;
  color: #424242;
  padding: 15px 20px;
  margin: 20px 0;
  border-radius: 4px;
}

.erc-api-status-icon {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #e53935;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  margin-right: 15px;
}

.erc-api-status-content {
  flex-grow: 1;
}

.erc-api-status-title {
  margin: 0 0 5px 0;
  font-weight: 700;
  font-size: 16px;
  color: #c62828;
}

.erc-api-status-message {
  margin: 0;
  font-size: 14px;
}
</style>